import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase/client';
import { PhoneInput } from '../../../components/PhoneInput';

interface FormData {
  email: string;
  password: string;
  businessName: string;
  contactPhone: string;
  website: string;
}

export function SignUpForm() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
    businessName: '',
    contactPhone: '',
    website: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Sign up with metadata included
      const { error: signUpError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            business_name: formData.businessName,
            contact_phone: formData.contactPhone,
            website: formData.website
          },
          emailRedirectTo: `${window.location.origin}/auth/callback`
        }
      });

      if (signUpError) throw signUpError;

      // Redirect to dashboard
      navigate('/dashboard');
    } catch (err) {
      console.error('Error during sign up:', err);
      setError(
        err instanceof Error 
          ? err.message 
          : 'An error occurred during sign up. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-500/20 backdrop-blur-sm border border-red-500/30 text-red-300 px-4 py-3 rounded-xl">
          {error}
        </div>
      )}

      <div>
        <label htmlFor="businessName" className="block text-sm font-medium text-white/90 mb-2">
          Business Name
        </label>
        <input
          id="businessName"
          name="businessName"
          type="text"
          required
          value={formData.businessName}
          onChange={handleChange}
          className="w-full rounded-xl border border-white/20 px-4 py-4 bg-white/10 backdrop-blur-sm text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
          placeholder="Your business name"
        />
      </div>

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-white/90 mb-2">
          Email Address
        </label>
        <input
          id="email"
          name="email"
          type="email"
          required
          value={formData.email}
          onChange={handleChange}
          className="w-full rounded-xl border border-white/20 px-4 py-4 bg-white/10 backdrop-blur-sm text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
          placeholder="your@email.com"
        />
      </div>

      <div>
        <label htmlFor="password" className="block text-sm font-medium text-white/90 mb-2">
          Password
        </label>
        <input
          id="password"
          name="password"
          type="password"
          required
          minLength={6}
          value={formData.password}
          onChange={handleChange}
          className="w-full rounded-xl border border-white/20 px-4 py-4 bg-white/10 backdrop-blur-sm text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
          placeholder="Create a secure password"
        />
        <p className="mt-2 text-sm text-white/60">
          Must be at least 6 characters
        </p>
      </div>

      <div>
        <label htmlFor="contactPhone" className="block text-sm font-medium text-white/90 mb-2">
          Contact Phone <span className="text-white/50">(optional)</span>
        </label>
        <PhoneInput
          value={formData.contactPhone}
          onChange={(value) => setFormData(prev => ({ ...prev, contactPhone: value }))}
          className="w-full rounded-xl border border-white/20 px-4 py-4 bg-white/10 backdrop-blur-sm text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
        />
      </div>

      <div>
        <label htmlFor="website" className="block text-sm font-medium text-white/90 mb-2">
          Website <span className="text-white/50">(optional)</span>
        </label>
        <input
          id="website"
          name="website"
          type="url"
          value={formData.website}
          onChange={handleChange}
          placeholder="https://yourwebsite.com"
          className="w-full rounded-xl border border-white/20 px-4 py-4 bg-white/10 backdrop-blur-sm text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
        />
      </div>

      <div className="space-y-6">
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-gradient-to-r from-primary-500 to-blue-600 hover:from-primary-400 hover:to-blue-500 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:shadow-none"
        >
          {loading ? (
            <div className="flex items-center justify-center gap-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Creating account...
            </div>
          ) : (
            'Create account'
          )}
        </button>

        <p className="text-xs text-center text-white/60 leading-relaxed">
          By signing up, you agree to our{' '}
          <Link to="/terms" className="text-primary-300 hover:text-primary-200 transition-colors">
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link to="/privacy" className="text-primary-300 hover:text-primary-200 transition-colors">
            Privacy Policy
          </Link>
        </p>
      </div>
    </form>
  );
}